import screenshot1 from '../assets/1.png';
import screenshot2 from '../assets/2.png';
import screenshot3 from '../assets/3.png';
import screenshot4 from '../assets/4.png';
import screenshot5 from '../assets/5.png';

export const APP_STORE_URL = 'https://apps.apple.com/us/app/peticle/id6766522201';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.unit326.peticle&hl=en';

export const navLinks = [
    { href: '/#how', label: 'How it works' },
    { href: '/#screens', label: 'Tour' },
    { href: '/#features', label: 'Features' },
    { href: '/#pricing', label: 'Pricing' },
    { href: '/#faq', label: 'FAQ' },
];

export const species = [
    { icon: '🐕', label: 'Dogs' },
    { icon: '🐈', label: 'Cats' },
    { icon: '🐰', label: 'Rabbits' },
    { icon: '🐦', label: 'Birds' },
    { icon: '🐠', label: 'Fish' },
    { icon: '🐹', label: 'Hamsters' },
];

export const steps = [
    {
        n: '01',
        title: 'Add your pets',
        description: 'Create a profile for each companion — species, sex, birthday, weight, microchip ID and notes. Metric or US units.',
    },
    {
        n: '02',
        title: 'Snap a photo',
        description: 'Run a breed, health or fecal scan straight from your camera or photo library. Add up to 3 photos for sharper breed results.',
    },
    {
        n: '03',
        title: 'Get insights & track',
        description: 'Receive a clear, plain-language summary with an urgency level, then log it alongside weights, vet visits and reminders.',
    },
];

export const showcase = [
    {
        image: screenshot1,
        tag: 'Pets',
        title: 'Every companion, one place',
        description: 'A tidy home for all your pets with breed, age and weight at a glance. Tap any profile for its full health history.',
    },
    {
        image: screenshot3,
        tag: 'Breed analysis',
        title: 'Know what makes them, them',
        description: 'A breed composition breakdown with confidence scores and the physical traits behind each match — plus temperament, diet and training tips.',
    },
    {
        image: screenshot4,
        tag: 'Fecal scan',
        title: 'Insights at your fingertips',
        description: 'Colour, consistency, blood, mucus, parasites and foreign objects — each checked and explained, with a clear call on how urgent it is.',
    },
    {
        image: screenshot2,
        tag: 'Health',
        title: 'A living health log',
        description: 'See each pet\'s latest status — Normal, Concerning or Urgent — log symptoms, browse past scans and start a new one in a tap.',
    },
    {
        image: screenshot5,
        tag: 'Reminders',
        title: 'Never miss a dose or visit',
        description: 'Recurring reminders for vaccines, tick & flea, deworming and check-ups. Mark them done and the next one schedules itself.',
    },
];

export type Feature = {
    icon: string;
    title: string;
    description: string;
    points?: string[];
    span?: 'wide';
};

export const features: Feature[] = [
    {
        icon: '🧬',
        title: 'Breed analysis',
        description: 'Curious about your rescue\'s mix? Get a breed breakdown with confidence scores from one to three photos.',
        points: ['Breed composition %', 'Temperament profile', 'Diet guidance', 'Training tips'],
        span: 'wide',
    },
    {
        icon: '🩺',
        title: 'Health scans',
        description: 'AI observations on coat, eyes, skin and visible health indicators — rated Normal, Concerning or Urgent.',
    },
    {
        icon: '📝',
        title: 'Symptom log',
        description: 'Not eating, vomiting, limping, anxiety, excessive drinking? Log what you see, rate severity and get tailored vet guidance.',
    },
    {
        icon: '🔬',
        title: 'Fecal analysis',
        description: 'Photograph stool and get a detailed read on the key indicators, tracked over time in a fecal history.',
        points: ['Colour', 'Consistency score', 'Blood', 'Mucus', 'Parasites', 'Foreign objects'],
        span: 'wide',
    },
    {
        icon: '⚖️',
        title: 'Weight tracking',
        description: 'Log weigh-ins with notes and watch the trend chart — sudden loss or gain is easy to spot.',
    },
    {
        icon: '🏥',
        title: 'Vet visit records',
        description: 'Clinic, vet, reason, diagnosis and weight at visit — plus vaccinations with batch numbers, medications and test results.',
    },
    {
        icon: '🔔',
        title: 'Smart reminders',
        description: 'One-off or repeating every 1, 3, 6 or 12 months. Tick them off and the next one schedules itself.',
        points: ['Vaccines', 'Tick & flea', 'Deworming', 'Vet visits', 'Custom'],
        span: 'wide',
    },
    {
        icon: '🔐',
        title: 'Private by design',
        description: 'Your pets\' data stays yours. Request a copy or delete everything at any time.',
        points: ['Apple & Google sign-in', 'Encrypted in transit', 'Never sold', 'Delete anytime'],
        span: 'wide',
    },
];

export const urgencyLevels = [
    { label: 'Routine', desc: 'Looks normal — keep doing what you\'re doing.', color: '#4A7C59', bg: '#EAF3DE' },
    { label: 'Monitor', desc: 'Worth watching. Re-check in a few days.', color: '#B7791F', bg: '#FEF3C7' },
    { label: 'Vet soon', desc: 'Book an appointment in the coming days.', color: '#C2410C', bg: '#FFEDD5' },
    { label: 'Emergency', desc: 'Contact a vet immediately.', color: '#DC2626', bg: '#FEE2E2' },
];

export type Plan = {
    id: 'free' | 'starter' | 'pro' | 'breeder';
    name: string;
    emoji: string;
    tagline: string;
    monthly: number;
    annual: number;
    color: string;
    features: string[];
    highlight?: boolean;
};

// Mirrors backend/src/subscription/plans.ts (PLAN_LIMITS + PLAN_PRICING).
export const plans: Plan[] = [
    {
        id: 'free',
        name: 'Free',
        emoji: '🐾',
        tagline: 'Try Peticle with one pet',
        monthly: 0,
        annual: 0,
        color: '#6B6B5A',
        features: ['3 analyses / month', '1 pet', 'Breed insights', 'Reminders, weights & vet logs'],
    },
    {
        id: 'starter',
        name: 'Starter',
        emoji: '🌿',
        tagline: 'For a small household',
        monthly: 9.99,
        annual: 99.99,
        color: '#4A7C59',
        features: ['15 analyses / month', 'Up to 3 pets', 'Breed analysis', 'Health scans', 'Unused analyses roll over'],
    },
    {
        id: 'pro',
        name: 'Pro',
        emoji: '⭐',
        tagline: 'For the dedicated pet parent',
        monthly: 24.99,
        annual: 249.99,
        color: '#2D6A8F',
        highlight: true,
        features: ['60 analyses / month', 'Unlimited pets', 'All scan types', 'Priority AI processing', 'Unused analyses roll over'],
    },
    {
        id: 'breeder',
        name: 'Breeder',
        emoji: '🏆',
        tagline: 'For breeders & shelters',
        monthly: 59.99,
        annual: 599.99,
        color: '#7B3FA0',
        features: ['300 analyses / month', 'Unlimited pets', 'All scan types', 'Bulk analysis tools', 'Unused analyses roll over'],
    },
];

export const faqs = [
    {
        q: 'Does Peticle replace my vet?',
        a: 'No. Peticle provides informational insights only — it is not a veterinary diagnostic tool. Think of it as an extra pair of eyes between visits that helps you decide when to call your vet, and gives you organised history to share when you do.',
    },
    {
        q: 'Which pets are supported?',
        a: 'You can create profiles for dogs, cats, rabbits, birds, fish and hamsters, and manage several pets from one account.',
    },
    {
        q: 'What counts as an analysis?',
        a: 'Each AI result — a breed analysis, fecal scan or symptom assessment — uses one analysis from your monthly allowance. Health and fecal scans are available on Starter and above. Logging weights, vet visits and reminders doesn\'t use analyses.',
    },
    {
        q: 'What happens to analyses I don\'t use?',
        a: 'On paid plans, unused analyses roll over to the next month, up to one month\'s allowance. Nothing goes to waste on a quiet month.',
    },
    {
        q: 'Can I switch or cancel my plan?',
        a: 'Yes. Subscriptions are managed through the App Store or Google Play, so you can upgrade, downgrade or cancel at any time from your store account. Annual billing saves about 17%.',
    },
    {
        q: 'What happens to my pet photos?',
        a: 'Photos are sent securely over HTTPS, used only to generate your pet\'s results and history, and never sold or used for advertising. You can delete scans, pets or your whole account at any time.',
    },
];
